import type { ExpenseCategory, CashFlowPoint, Transaction } from './types';
import { CATEGORY_PALETTE } from './tokens';

// Full currency formatter (USD/CAD style; locale respected)
export function fmtMoney(amount: number): string {
  return amount.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Short money format for axis labels: $1.2K, $12K, $1.2M
export function fmtMoneyShort(amount: number): string {
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return '$' + (amount / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1_000)     return '$' + (amount / 1_000).toFixed(abs >= 10_000 ? 0 : 1) + 'K';
  return '$' + amount.toFixed(0);
}

// Signed delta: "+12%", "-8%", "0%"
export function fmtPct(pct: number): string {
  if (pct === 0) return '0%';
  const sign = pct > 0 ? '+' : '';
  return sign + pct.toFixed(0) + '%';
}

// Pretty date for a transaction row: "Oct 5"
export function fmtShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

// Month label for cash-flow x-axis: "Jan" or "Jan '26"
export function fmtMonthLabel(monthIso: string, withYear: boolean = false): string {
  const [y, m] = monthIso.split('-').map(Number);
  if (!y || !m) return monthIso;
  const d = new Date(y, m - 1, 1);
  const base = d.toLocaleDateString('en-US', { month: 'short' });
  return withYear ? base + " '" + String(y).slice(2) : base;
}

// Map an expense category to a palette color (stable by index)
export function categoryColor(index: number): string {
  return CATEGORY_PALETTE[index % CATEGORY_PALETTE.length];
}

// Turn raw category totals into ExpenseCategory[] with percentages, sorted desc
export function toExpenseCategories(
  rows: ReadonlyArray<{ label: string; amount: number }>
): ExpenseCategory[] {
  const total = rows.reduce((s, r) => s + Math.max(0, r.amount), 0);
  if (total <= 0) {
    return rows.map((r, i) => ({ label: r.label, amount: r.amount, pct: 0, color: categoryColor(i) }));
  }
  return [...rows]
    .sort((a, b) => b.amount - a.amount)
    .map((r, i) => ({
      label: r.label,
      amount: r.amount,
      pct: Math.round((r.amount / total) * 100),
      color: categoryColor(i),
    }));
}

// Net flow helper for a month
export function netFlow(point: CashFlowPoint): number {
  return point.moneyIn - point.moneyOut;
}

// Sum of a signed transaction list (ins minus outs)
export function sumTransactions(txns: ReadonlyArray<Transaction>): number {
  return txns.reduce((s, t) => s + t.amount, 0);
}
